import { Vue } from 'vue-property-decorator';
import { HubConnectionBuilder, HubConnection, LogLevel, HubConnectionState } from '@microsoft/signalr';
import { PluginFunction, PluginObject } from 'vue';

// Largely copied over from the vue.js 2 - attmag
export default class BackendHub extends Vue {
  constructor(plugin: PluginObject<unknown> | PluginFunction<unknown>) {
    super(plugin);
  }

  static install(): void {
    // use new Vue instance as an event bus
    const backendHub = new Vue();
    // every component will use this.$backendHub to access the event bus
    Vue.prototype.$backendHub = backendHub;
    // Provide methods to connect/disconnect from the SignalR hub
    let connection: null | HubConnection = null;
    let startedPromise: Promise<void> | null = null;
    let connectionPromise: Promise<void> | null = null;
    let manuallyClosed = false;

    // ========================================================================
    //  Handler Registration (CRITICAL: Called on EVERY connection)
    // ========================================================================
    //
    // WHY THIS MATTERS:
    // SignalR handlers must be re-registered after EVERY connection, including
    // auto-reconnects. If we only register once, handlers are lost when the
    // connection drops and reconnects.
    //
    // HOW IT WORKS:
    // 1. .off() removes any existing handlers for this event (prevents duplicates)
    // 2. .on() registers our handler that forwards the event to the Vue event bus
    // 3. This function is called BOTH on initial connect AND on every reconnection
    //
    function registerHandlers(conn: HubConnection): void {
      // Remove existing handlers first (idempotent cleanup)
      // This prevents "handler accumulation" if registerHandlers is called multiple times
      conn.off('UpdateUserRoles');
      conn.off('UpdateUserRolesMultiple');
      conn.off('UpdateContact');
      conn.off('UpdateAdmpNewsCount');
      conn.off('UpdateListAdmpNews');
      conn.off('UpdateGspModuleName');

      //  Register fresh handlers that forward to Vue event bus
      // Components listen to these Vue events, not directly to SignalR
      conn.on('UpdateUserRoles', (contactId: string) => {
        // console.log(`SignalR: UpdateUserRoles for contact ${contactId}`);
        backendHub.$emit('update:user-roles', contactId);
      });

      conn.on('UpdateUserRolesMultiple', (contactIds: string[]) => {
        // console.log(` SignalR: UpdateUserRolesMultiple for ${contactIds.length} contacts`);
        backendHub.$emit('update:user-roles-multiple', contactIds);
      });

      conn.on('UpdateContact', (contactId: string) => {
        // console.log(` SignalR: UpdateContact for contact ${contactId}`);
        backendHub.$emit('update:contact', contactId);
      });

      conn.on('UpdateAdmpNewsCount', () => {
        backendHub.$emit('update:admp-news-count');
      });

      conn.on('UpdateListAdmpNews', () => {
        backendHub.$emit('update:list-admp-news');
      });

      // forwards GspModule rename events (id + name) from the server
      conn.on('UpdateGspModuleName', (id: number, name: string) => {
        // console.log(`SignalR: UpdateGspModuleName id=${id} name=${name}`);
        backendHub.$emit('update:gsp-module-name', id, name);
      });

      console.log('✅ SignalR handlers registered');
    }

    function startConnection(): Promise<void> {
      // If we already have a connected connection, return immediately
      if (
        connection?.state === HubConnectionState.Connected ||
        connection?.state === HubConnectionState.Connecting ||
        connection?.state === HubConnectionState.Reconnecting
      ) {
        console.log('🔗 SignalR already connected(-ing), reusing connection');
        return Promise.resolve();
      }

      // If a connection attempt is already in progress, return that promise
      // This prevents multiple parallel connection attempts (race condition)
      if (connectionPromise) {
        console.log('🔗 SignalR connection in progress, waiting for existing promise');
        return connectionPromise;
      }

      // CREATE NEW CONNECTION (only if we don't have one)
      if (!connection) {
        console.log('Creating new SignalR connection...');

        connection = new HubConnectionBuilder()
          .withUrl('/signalr/BackendHub')
          // .withAutomaticReconnect([0, 2000, 5000, 10000, 30000]) // Smart retry delays // !! seems to be conflicting with our custom reconnect logic, so using manual for now
          .configureLogging(LogLevel.Warning)
          .build();

        // Register handlers on the NEW connection
        // This must happen BEFORE start() to catch events during connection
        registerHandlers(connection);

        // Handle automatic reconnection: re-register handlers when reconnected
        // SignalR's withAutomaticReconnect handles the reconnect logic,
        // but we must re-register our custom handlers each time
        connection.onreconnecting((error) => {
          console.log('🔄 SignalR reconnecting...', error);
        });

        connection.onreconnected((connectionId) => {
          console.log('✅ SignalR reconnected with ID:', connectionId);
          //  RE-REGISTER HANDLERS after reconnection (!! important)
          if (connection) {
            registerHandlers(connection);
          }
        });

        connection.onclose((error) => {
          console.log('❌ SignalR connection closed', error);
          // Only auto-reconnect if the user didn't manually disconnect
          if (!manuallyClosed) {
            console.log('🔄 Auto-reconnecting in 3 seconds...');
            // Reset state so a new connection can be created
            connection = null;
            connectionPromise = null;
            // Use setTimeout to avoid tight reconnect loops
            setTimeout(() => {
              if (!manuallyClosed) {
                startConnection().catch((err) => {
                  console.error('🔄 Auto-reconnect failed:', err);
                });
              }
            }, 3000);
          }
        });
      }

      // START THE CONNECTION and cache the promise
      // SignalR's start() promise resolves when connection is ESTABLISHED
      // (no need for manual polling!)
      connectionPromise = connection
        .start()
        .then(() => {
          connectionPromise = null;
          console.log('✅ SignalR connected successfully');
          // Connection is ready - handlers already registered above
        })
        .catch((err: any) => {
          console.error('❌ Failed to connect to SignalR hub:', err);
          //  CRITICAL: Reset state on failure so next attempt can try again
          connection = null;
          connectionPromise = null;
          // Re-throw so callers can handle the error if needed
          throw err;
        });

      return connectionPromise;
    }

    async function stopConnection(): Promise<void> {
      if (!connection) {
        console.log('⚠️ stopSignalR called but no connection exists');
        return;
      }

      console.log('🛑 Manually stopping SignalR connection...');
      manuallyClosed = true;

      try {
        // Clean up handlers to prevent memory leaks
        connection.off('UpdateUserRoles');
        connection.off('UpdateUserRolesMultiple');
        connection.off('UpdateContact');
        connection.off('UpdateAdmpNewsCount');
        connection.off('UpdateListAdmpNews');
        connection.off('UpdateGspModuleName');

        // Gracefully stop the connection
        await connection.stop();
        console.log('✅ SignalR connection stopped');
      } catch (err) {
        console.error('❌ Error stopping SignalR:', err);
        throw err; // Let caller handle if needed
      } finally {
        // Reset ALL state variables to allow fresh start later
        connection = null;
        connectionPromise = null;
        manuallyClosed = false;
        console.log('SignalR state reset');
      }
    }

    // ========================================================================
    // 🌍 Expose Methods to Vue Components
    // ========================================================================
    // Components call these via: this.$root.startSignalR() or this.$root.stopSignalR()

    Vue.prototype.startSignalR = (): Promise<void> => {
      manuallyClosed = false; // Reset manual close flag on explicit start
      return startConnection();
    };

    Vue.prototype.stopSignalR = (): Promise<void> => {
      return stopConnection();
    };

    // Optional: Expose connection state for debugging
    Vue.prototype.isSignalRConnected = (): boolean => {
      return connection?.state === HubConnectionState.Connected;
    };

    //#region OLD implementation
    // Vue.prototype.startSignalR = () => {
    //   // GUARD: If a connection already exists or is starting, do nothing.
    //   if (connection) {
    //     if (
    //       connection.state === HubConnectionState.Connected ||
    //       connection.state === HubConnectionState.Connecting
    //     ) {
    //       console.warn('SignalR connection already exists or is in progress.');
    //       return startedPromise;
    //     }
    //   }

    //   connection = new HubConnectionBuilder()
    //     .withUrl(`/signalr/BackendHub`)
    //     .configureLogging(LogLevel.Information)
    //     .build();

    //   registerHandlers(connection);
    //   // /*
    //   //  * Checking for connected state by polling the value of the connection.state
    //   //  * This is very inconvenient, but I currently do not know another way to
    //   //  * determine if a connection has been established.
    //   //  * startedPromise cannot be used, as it is fulfilled before the connection has been established
    //   //  */
    //   // connectionPromise = new Promise((resolve, reject) => {
    //   //   let connectedCheck = () => {
    //   //     if (connection?.state == HubConnectionState.Connected) {
    //   //       resolve();
    //   //       return;
    //   //     }
    //   //     setTimeout(connectedCheck, 100);
    //   //   };
    //   //   connectedCheck();
    //   // });

    //   function start() {
    //     startedPromise = connection!.start().catch((err: any) => {
    //       console.error('Failed to connect with hub', err);
    //     });
    //     return startedPromise;
    //   }

    //   connection.onclose(() => {
    //     if (!manuallyClosed) start();
    //   });

    //   manuallyClosed = false;
    //   start();
    // };

    // Vue.prototype.stopSignalR = async () => {
    //   if (!connection) return;

    //   manuallyClosed = true;

    //   try {
    //     // Remove the SignalR listeners to prevent memory leaks/duplicates
    //     connection.off('UpdateUserRoles');
    //     connection.off('UpdateUserRolesMultiple');
    //     connection.off('UpdateContact');

    //     await connection.stop();
    //   } catch (err) {
    //     console.error('Error stopping SignalR', err);
    //   } finally {
    //     // Reset all state variables
    //     connection = null;
    //     startedPromise = null;
    //     connectionPromise = null;
    //   }
    // };
    //#endregion
  }
}